import { z } from 'zod';
import { countSmsSegments, renderSmsTemplate, type SmsSegmentInfo } from './welcome-sms.js';

/**
 * SMS préétablis du vendeur — réunion Doorly du 11/09/2026.
 *
 * Quand le centre d'appel n'arrive pas à joindre le prospect, le vendeur peut
 * lui écrire **depuis son propre téléphone** : Doorly ne fait qu'ouvrir
 * Messages avec le texte prêt (lien `sms:`), il n'envoie rien et ne consomme
 * aucun crédit. Décision explicite : « c'est lui qui gère sa relation
 * client », et un SMS parti d'un numéro que le prospect a vu à sa porte est
 * lu, là où nos numéros de plateforme ne le sont pas toujours.
 *
 * Deux règles tiennent le dispositif :
 *  - **un segment** (160 caractères GSM-7) : le texte doit arriver entier et
 *    propre ; d'où des modèles courts et sans caractère hors alphabet GSM-7
 *    (pas de ç, ê, â, ô, î, ni guillemets typographiques) ;
 *  - **deux SMS par fiche** : au-delà, on passe au harcèlement. Le vendeur
 *    reste libre d'écrire ce qu'il veut depuis son téléphone — on « brise le
 *    bouton », on ne surveille pas sa messagerie.
 *
 * Quand relancer a un sens : `CALLCENTER_IN_PROGRESS_STATUSES` (enums/lead-status),
 * la même frontière que les stories « à prévenir ».
 */

export const VENDOR_SMS_MAX_PER_LEAD = 2;

export const VENDOR_SMS_TEMPLATE_KEYS = ['unanswered', 'call_back', 'unknown_number'] as const;
export const VendorSmsTemplateKeySchema = z.enum(VENDOR_SMS_TEMPLATE_KEYS);
export type VendorSmsTemplateKey = z.infer<typeof VendorSmsTemplateKeySchema>;

/** Valeurs injectables. Une valeur vide = placeholder absent (bloc `[[…]]` retiré). */
export interface VendorSmsVars {
  /** Prénom du prospect. */
  prenom: string;
  /** Prénom du vendeur — c'est lui qui écrit, de son numéro. Peut manquer hors ligne. */
  vendeur: string;
  /** Marque que le prospect connaît (`deliveryPartnerBrand`), sinon vide. */
  marque: string;
  /** Tentatives d'appel du centre, si connues. */
  n: string;
  /** Numéro miroir du partenaire, formaté pour être composé. */
  numeroMiroir: string;
  /** « du lundi au vendredi de 9h à 19h », si le partenaire a des horaires. */
  horaires: string;
}

export interface VendorSmsTemplate {
  key: VendorSmsTemplateKey;
  /** Libellé court dans la liste des modèles. */
  label: string;
  /** Quand l'utiliser, en une ligne. */
  hint: string;
  /** Placeholders sans lesquels le modèle n'a pas de sens : il est masqué. */
  requires: ReadonlyArray<keyof VendorSmsVars>;
  template: string;
}

/**
 * Les trois modèles. `[[ … ]]` = bloc retiré si un placeholder qu'il contient
 * est vide (même convention que le SMS de bienvenue). Le vendeur se présente
 * par son prénom et la marque : le prospect l'a vu à sa porte, pas Doorly.
 * Le prénom du vendeur est lui aussi optionnel : hors ligne à froid il peut
 * manquer, et « Bonjour Jean,. » ne doit jamais partir.
 */
export const VENDOR_SMS_TEMPLATES: ReadonlyArray<VendorSmsTemplate> = [
  {
    key: 'unanswered',
    label: 'Il ne décroche pas',
    hint: 'Le centre a appelé sans succès : prévenir qu’on rappelle aujourd’hui.',
    requires: [],
    template:
      "Bonjour {prenom}[[, {vendeur}]][[ ({marque})]]. Impossible de vous joindre[[ ({n} appels)]]. Un conseiller vous rappelle aujourd'hui, merci de décrocher !",
  },
  {
    key: 'call_back',
    label: 'Rappelez-nous',
    hint: 'Donner le numéro à composer et les horaires.',
    requires: ['numeroMiroir'],
    template:
      'Bonjour {prenom}[[, {vendeur}]][[ ({marque})]]. Pour votre demande, un conseiller vous répond au {numeroMiroir}[[, {horaires}]]. Merci !',
  },
  {
    key: 'unknown_number',
    label: 'Numéro inconnu',
    hint: 'L’appel viendra d’un numéro qu’il ne connaît pas : le rassurer.',
    requires: [],
    template:
      "Bonjour {prenom}[[, {vendeur}]][[ ({marque})]]. Un conseiller va vous appeler d'un numéro inconnu : c'est bien pour votre demande, merci de répondre !",
  },
];

export interface VendorSmsMessage extends VendorSmsTemplate {
  text: string;
  info: SmsSegmentInfo;
}

/** Les modèles rendus pour une fiche, sans ceux dont il manque l'essentiel. */
export function buildVendorSmsMessages(vars: VendorSmsVars): VendorSmsMessage[] {
  return VENDOR_SMS_TEMPLATES.filter((t) => t.requires.every((k) => vars[k].trim().length > 0)).map(
    (t) => {
      const text = renderSmsTemplate(t.template, { ...vars });
      return { ...t, text, info: countSmsSegments(text) };
    },
  );
}

const DAY_FR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** `"09:00"` → `9h`, `"18:30"` → `18h30`. Compact : chaque caractère compte. */
function hourFr(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return hhmm.trim();
  const h = String(Number(m[1]));
  return m[2] === '00' ? `${h}h` : `${h}h${m[2]}`;
}

/**
 * Horaires d'ouverture lisibles dans un SMS : « du lundi au vendredi de 9h à
 * 19h », « le lundi, mercredi et samedi de 9h à 12h »… Null si le partenaire
 * n'a pas d'horaires (ou pas de partenaire) — le bloc `[[…]]` qui les porte
 * disparaît alors.
 */
export function formatOpeningHoursFr(
  opening:
    | {
        openingStart?: string | null;
        openingEnd?: string | null;
        openingDays?: ReadonlyArray<number> | null;
      }
    | null
    | undefined,
): string | null {
  if (!opening?.openingStart || !opening.openingEnd) return null;
  const days = [...new Set((opening.openingDays ?? []).filter((d) => d >= 1 && d <= 6))].sort(
    (a, b) => a - b,
  );
  const hours = `de ${hourFr(opening.openingStart)} à ${hourFr(opening.openingEnd)}`;
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) return hours;
  if (days.length === 1) return `le ${DAY_FR[first]} ${hours}`;
  if (days.length === last - first + 1) return `du ${DAY_FR[first]} au ${DAY_FR[last]} ${hours}`;
  const names = days.map((d) => DAY_FR[d]);
  return `le ${names.slice(0, -1).join(', ')} et ${names[names.length - 1]} ${hours}`;
}

// ── API ─────────────────────────────────────────────────────

/** `POST /leads/:id/vendor-sms` — le vendeur a ouvert Messages avec ce modèle. */
export const PrepareVendorSmsSchema = z.object({
  template: VendorSmsTemplateKeySchema,
  /** Segments du texte rendu côté client (information, jamais une garde). */
  segments: z.number().int().min(1).max(10).optional(),
});
export type PrepareVendorSmsInput = z.infer<typeof PrepareVendorSmsSchema>;

/** Compteur renvoyé : ce qui a été préparé et ce qui reste sur la fiche. */
export const VendorSmsQuotaSchema = z.object({
  sent: z.number().int().min(0),
  remaining: z.number().int().min(0),
  max: z.number().int().min(1),
});
export type VendorSmsQuota = z.infer<typeof VendorSmsQuotaSchema>;

export const VENDOR_SMS_ERROR_CODES = {
  /** Les deux SMS de la fiche ont déjà été utilisés (409). */
  QUOTA_REACHED: 'VENDOR_SMS_QUOTA_REACHED',
  /** Fiche sans téléphone, archivée ou déjà terminale (400). */
  NOT_APPLICABLE: 'VENDOR_SMS_NOT_APPLICABLE',
} as const;
